// ─── RBAC permission catalog ──────────────────────────────────────
// Single source of truth for what a role can grant. The API enforces these
// keys via requirePermission() preHandlers; the SPA gates UI via can().
// Keys are code-defined (never created at runtime); roles stored in the DB
// reference them by string. Adding a key here makes it admin-grantable —
// until granted, new capabilities are Administrator-only via the wildcard.
/** Granted only to the seeded system Administrator role; never settable via the admin API. */
export const PERMISSION_WILDCARD = '*';
/** Grouped catalog — drives the role-editor UI checkboxes and labels. */
export const PERMISSION_GROUPS = [
    {
        area: 'tickets',
        label: 'Tickets (Zoho Desk)',
        permissions: [
            { key: 'tickets.reply', label: 'Send replies', hint: 'Send customer-facing email replies on tickets' },
            { key: 'tickets.comment', label: 'Add comments', hint: 'Add internal (agent-only) comments to tickets' },
            { key: 'tickets.update', label: 'Update tickets', hint: 'Change ticket status, assignee, subject and priority' },
        ],
    },
    {
        area: 'topics',
        label: 'Topics (Discourse)',
        permissions: [
            { key: 'topics.reply', label: 'Post replies', hint: 'Post public replies, create topics, edit posts and rename topics' },
            { key: 'topics.tags', label: 'Edit tags', hint: 'Apply or replace topic tags' },
            { key: 'topics.moderate', label: 'Moderate topics', hint: 'Accept/unaccept solutions, nudge still-relevant, close stale topics' },
        ],
    },
    {
        area: 'backlog',
        label: 'Backlog (Azure DevOps)',
        permissions: [
            { key: 'backlog.write', label: 'Update backlog items', hint: 'Change state, assignee, order; add, edit and delete your own discussion comments; upload attachments' },
            { key: 'backlog.create', label: 'Create work items', hint: 'Create bugs/stories from tickets, topics and authoring' },
        ],
    },
    {
        area: 'feedback',
        label: 'Feedback boards',
        permissions: [
            { key: 'feedback.write', label: 'Create & annotate boards', hint: 'Create/edit Feedback boards, add and move screenshots, place comment pins, and edit or delete your own comments' },
            { key: 'feedback.manage', label: 'Manage all feedback', hint: 'Delete any board and edit or delete anyone’s comment or reply (the full permission)' },
        ],
    },
    {
        area: 'filters',
        label: 'Saved filters',
        permissions: [
            { key: 'filters.share', label: 'Share filters', hint: 'Share your own saved filters with everyone (personal filters, pins and the filter bar layout need no permission)' },
            { key: 'filters.editShared', label: 'Edit shared filters', hint: 'Change the conditions, name, icon, colour and display of filters others shared, and restore their earlier versions' },
            { key: 'filters.manage', label: 'Manage all filters', hint: 'Delete, unshare or undelete anyone’s saved filter (the full permission)' },
        ],
    },
    {
        area: 'requests',
        label: 'Feature & bug requests',
        permissions: [
            { key: 'requests.create', label: 'Create requests', hint: 'Submit new feature or bug requests for PCP (the submitter can always edit + delete their own)' },
            { key: 'requests.vote', label: 'Vote on requests', hint: 'Upvote (or remove an upvote from) a request' },
            { key: 'requests.manage', label: 'Manage requests', hint: 'Edit/delete ANY request and change its type + lifecycle status (the full permission)' },
            { key: 'releases.manage', label: 'Manage release notes', hint: 'Record shipped items, AI-draft, edit and publish release notes (the "what\'s new" announcements)' },
        ],
    },
    {
        area: 'status',
        label: 'Status page',
        permissions: [
            { key: 'status.publish', label: 'Publish incidents', hint: 'Create and update incidents, maintenance and information posts, set a component\'s status and notify subscribers' },
            { key: 'status.manage', label: 'Manage status page', hint: 'Configure audiences, component groups and components; delete incidents; manage subscribers' },
        ],
    },
    {
        area: 'crm',
        label: 'Companies (Zoho CRM)',
        permissions: [
            { key: 'crm.create', label: 'Create records', hint: 'Create accounts, contacts and notes in Zoho CRM' },
            { key: 'crm.write', label: 'Edit records', hint: 'Edit accounts/contacts/notes and set account↔tenant links' },
            { key: 'crm.delete', label: 'Delete records', hint: 'Delete contacts and notes' },
        ],
    },
    {
        area: 'comms',
        label: 'Communications Journal',
        permissions: [
            { key: 'comms.journalize', label: 'Journalize communications', hint: 'Manually journalize, re-assign or dismiss an incoming communication onto a customer (viewing the journal stays open)' },
            { key: 'comms.send', label: 'Send messages', hint: 'Send email and Teams chat messages on your own behalf via your connected Microsoft 365 account' },
        ],
    },
    {
        area: 'knowledge',
        label: 'Knowledge base',
        permissions: [
            { key: 'knowledge.author', label: 'Author content', hint: 'Create, edit and delete articles, documents and navigation' },
            { key: 'knowledge.publish', label: 'Publish content', hint: 'Publish or archive knowledge base articles' },
        ],
    },
    {
        area: 'guides',
        label: 'User Guides',
        permissions: [
            { key: 'guides.author', label: 'Author guides', hint: 'Create, edit, publish and archive user-guide apps, guides and steps' },
        ],
    },
    {
        area: 'entitlements',
        label: 'Entitlements',
        permissions: [
            { key: 'entitlements.manage', label: 'Manage entitlements', hint: 'Create/edit entitlements and groups; accept, reject or generate suggestions' },
        ],
    },
    {
        area: 'ai',
        label: 'AI',
        permissions: [
            { key: 'ai.use', label: 'Use AI tools', hint: 'AI chat, drafting, analysis, next steps and suggestions (token spend)' },
            { key: 'playbooks.manage', label: 'Manage global playbooks', hint: 'Edit the shared playbooks and run distill; personal overrides need no permission' },
        ],
    },
    {
        area: 'conversations',
        label: 'Conversations',
        permissions: [
            { key: 'conversations.use', label: 'Use conversations', hint: 'Access the conversations area, start new chats and the Discuss action (sending messages also needs Use AI tools)' },
        ],
    },
    {
        area: 'insights',
        label: 'Insights',
        permissions: [
            { key: 'insights.overview', label: 'Overview tab', hint: 'See the Insights → Overview tab' },
            { key: 'insights.tickets', label: 'Tickets tab', hint: 'See the Insights → Tickets tab' },
            { key: 'insights.agents', label: 'Agents tab', hint: 'See the Insights → Agents tab' },
            { key: 'insights.drafts', label: 'AI drafts tab', hint: 'See the Insights → AI drafts tab' },
            { key: 'insights.employees', label: 'Employees tab', hint: 'See the Insights → Employees tab and employee detail pages' },
            { key: 'insights.engineering', label: 'Engineering tab', hint: 'See the Insights → Engineering tab' },
            { key: 'insights.community', label: 'Community tab', hint: 'See the Insights → Community tab' },
            { key: 'insights.ai', label: 'AI Impact tab', hint: 'See the Insights → AI Impact tab' },
            { key: 'insights.usage', label: 'Usage tab', hint: 'See the Insights → Usage tab' },
        ],
    },
    {
        area: 'operations',
        label: 'Operations',
        permissions: [
            { key: 'sync.run', label: 'Run syncs', hint: 'Trigger or stop integration syncs, resyncs and run-all' },
            { key: 'notifications.errors', label: 'Sync error alerts', hint: 'Opt into push notifications when an integration sync fails' },
            { key: 'maintenance.tools', label: 'Maintenance tools', hint: 'Backfills, re-embeddings, reindexing, imports' },
            { key: 'maintenance.danger', label: 'Danger zone', hint: 'Irreversible operations like clearing all synced data' },
        ],
    },
    {
        area: 'administration',
        label: 'Administration',
        permissions: [
            { key: 'settings.manage', label: 'Manage settings', hint: 'Edit app-wide settings and integration configuration' },
            { key: 'users.manage', label: 'Manage users & roles', hint: 'Assign roles, create/edit roles and their permissions' },
            { key: 'advanced.view', label: 'Advanced menu', hint: 'See the Advanced tools menu/page (power-user utilities)' },
            { key: 'diagnostics.view', label: 'View error log', hint: 'See the app-wide client + server error diagnostics' },
            { key: 'diagnostics.manage', label: 'Manage error log', hint: 'Mark collected errors resolved / clear them' },
        ],
    },
];
export const ALL_PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));
/**
 * Union permission check shared by API enforcement and the SPA.
 * Understands the Administrator wildcard.
 */
export function hasPermission(perms, perm) {
    return !!perms && (perms.includes(PERMISSION_WILDCARD) || perms.includes(perm));
}
//# sourceMappingURL=permissions.js.map